import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { NAV, NAV_ICONS } from './navConfig';
import { useAuth } from '../lib/auth';
import { useDetail } from '../lib/useDetail';
import { roleLabel } from '../lib/format';
import { can } from '../lib/permissions';
import type { NavCounts } from '../lib/types';
import { GlobalSearch } from '../components/GlobalSearch';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useFinanceGate } from '../modules/finance/FinanceGate';

function findTitle(pathname: string): string {
  for (const g of NAV) {
    for (const it of g.items) {
      if (it.to === pathname || (it.to !== '/' && pathname.startsWith(it.to))) return it.label;
    }
  }
  return '一体化业务平台';
}

function NavIcon({ name }: { name?: string }) {
  const path = name ? NAV_ICONS[name] : undefined;
  if (!path) return <span className="ic" aria-hidden="true" />;
  return (
    <span className="ic" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: path }} />
    </span>
  );
}

export function AppLayout() {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const financeGate = useFinanceGate();
  const title = useMemo(() => findTitle(location.pathname), [location.pathname]);
  const { data: counts } = useDetail<NavCounts>('/api/counts');

  // 最小权限：按当前角色 view 权限过滤侧栏入口；无模块归属（如经营总览）始终可见，空分组隐藏。
  // 另加一层**环境门控**：后端未启用的分组（gated）整组不渲染。
  // 与权限过滤是两件事 —— 权限回答「这个角色能不能看」，门控回答「这个环境有没有」；
  // 二者都通过才显示，避免在生产环境里挂出只会显示「未启用」的入口。
  const visibleGroups = useMemo(
    () => NAV
      .filter((g) => !(g.gated === 'financeRead' && financeGate !== 'enabled'))
      .map((g) => ({
      ...g,
      // adminOnly 优先：无 module 但受管理权限制的入口（如公司财税资料）。
      items: g.items.filter((it) => (it.adminOnly ? isAdmin : (!it.module || can(user, it.module, 'view')))),
    }))
      .filter((g) => g.items.length > 0),
    [user, isAdmin, financeGate],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const initials = (user?.display_name || user?.username || '?').slice(0, 1).toUpperCase();

  return (
    <div className="shell">
      <aside className="side">
        <div className="brand">
          <div className="brand-mark logo"><img src="/logo.png" alt="Pinnacle Laser" /></div>
          <div className="brand-text">
            <b>一体化业务平台</b>
            <span>Pinnacle Laser</span>
          </div>
        </div>
        <div className="side-scroll">
          {visibleGroups.map((g) => (
            <div className="nav-group" key={g.label}>
              <div className="nav-group-label">{g.label}</div>
              {g.items.map((it) => {
                const cnt = it.countKey && counts ? Number((counts as unknown as Record<string, number>)[it.countKey]) || 0 : 0;
                return it.external ? (
                  <a
                    key={it.to}
                    href={it.to}
                    target="_blank"
                    rel="noopener"
                    className="nav-item"
                    title={it.label}
                  >
                    <NavIcon name={it.icon} />
                    <span className="nav-label-txt">{it.label}</span>
                  </a>
                ) : (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === '/'}
                    className={({ isActive }) => `nav-item ${isActive ? 'on' : ''} ${it.wired ? '' : 'soon'}`.trim()}
                    title={it.wired ? it.label : '尚未接入本控制台'}
                  >
                    <NavIcon name={it.icon} />
                    <span className="nav-label-txt">{it.label}</span>
                    {cnt > 0
                      ? <span className={`cnt${it.hot ? ' hot' : ''}`}>{cnt > 999 ? `${Math.round(cnt / 1000)}k` : cnt}</span>
                      : (!it.wired ? <span className="cnt plan">计划</span> : null)}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </div>
        <div className="side-foot">Web Console · v0.1<br />数据源：core-api /api</div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div>
            <h1>{title}</h1>
          </div>
          <div className="top-actions">
            <button className="search-trigger" onClick={() => setSearchOpen(true)} aria-label="全局搜索">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" strokeLinecap="round" />
              </svg>
              全局搜索
              <kbd>⌘/Ctrl K</kbd>
            </button>
            <div className="user-chip">
              <div className="avatar">{initials}</div>
              <div className="user-meta">
                <b>{user?.display_name || user?.username}</b>
                <span>{roleLabel(user?.role)}</span>
              </div>
            </div>
            <button className="btn" onClick={logout}>退出</button>
          </div>
        </header>

        <div className="content">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
